import { getLogger } from "@utils/ermineLoggerUtils";
import { getPortalConfig } from "@utils/configurationUtils";
import {
    getDeviceBrowser,
    getDeviceModel,
    getDeviceOSName,
    getDeviceTouchInfo,
    getDeviceType,
    isInternalTestTraffic,
} from "@utils/userAgentUtils";
import {
    fetchStreamingURLCache,
    refreshStreamingURLCache,
} from "@utils/StreamingURLCache";
import { useSessionLaunchMetricStore } from "@hooks/useSessionLaunchMetricStore";
import { CookieList } from "@customTypes/cookies";
import { sendCreateRemoteBrowserCommand } from "@utils/ermineSsrClientUtil";
import {
    getBrowserLanguage,
    getBrowserTimeZone,
} from "@utils/localizationUtils";
import { getDeepLink } from "@utils/deepLinkUtils";
import { isEmptyList } from "@utils/commonUtils";
import { getFormattedSdkErrorMessage } from "@utils/errorUtils";
import { useMutation } from "@tanstack/react-query";
import { ServiceException } from "@smithy/smithy-client";
import { CreateRemoteBrowserResponse } from "@amzn/ermine-server-side-rendering-client";
import { MetricName } from "@constants/logging";
import { useGetLogger } from "@hooks/useGetLogger";

const publishDeviceMetrics = () => {
    const log = getLogger();

    log.publishMetricWithDimension(
        MetricName.DEVICE_BROWSER,
        getDeviceBrowser()
    );
    log.publishMetricWithDimension(MetricName.DEVICE_TYPE, getDeviceType());
    log.publishMetricWithDimension(MetricName.DEVICE_MODEL, getDeviceModel());
    log.publishMetricWithDimension(MetricName.DEVICE_OS, getDeviceOSName());
    log.publishMetricWithDimension(
        MetricName.DEVICE_TOUCHSCREEN,
        getDeviceTouchInfo()
    );
};

export const useGetStreamingUrl = () => {
    const log = useGetLogger();

    const setCachedSessionEndpointLoadedTimestampIfEmpty =
        useSessionLaunchMetricStore(
            (store) => store.setCachedSessionEndpointLoadedTimestampIfEmpty
        );
    const setSessionEndpointCreatedTimestampIfEmpty =
        useSessionLaunchMetricStore(
            (store) => store.setSessionEndpointCreatedTimestampIfEmpty
        );

    const mutationResult = useMutation<
        string,
        ServiceException & Error,
        CookieList
    >({
        mutationFn: async (cookies: CookieList): Promise<string> => {
            const cachedStreamingURL = fetchStreamingURLCache();

            if (cachedStreamingURL !== undefined) {
                const { streamingURL } = cachedStreamingURL;
                log.publishCounterMetric(MetricName.STREAMING_URL_CACHE_HIT);
                setCachedSessionEndpointLoadedTimestampIfEmpty();

                return streamingURL;
            }

            let data: CreateRemoteBrowserResponse;
            try {
                const portalConfig = getPortalConfig();
                data = await sendCreateRemoteBrowserCommand({
                    portalArn: portalConfig.portalArn,
                    sessionConfig: {
                        supportedLanguage: getBrowserLanguage(),
                        timeZone: getBrowserTimeZone(),
                        deepLink: getDeepLink(),
                    },
                    cookies: isEmptyList(cookies)
                        ? null
                        : JSON.stringify(cookies),
                });
            } catch (err) {
                await log.error(
                    err
                        ? getFormattedSdkErrorMessage(err, "OidcBrowserCreator")
                        : "Failed to create streaming endpoint"
                );
                throw err;
            }

            try {
                log.publishHttpStatusMetric(
                    MetricName.CREATE_REMOTE_BROWSER,
                    200
                );
                setSessionEndpointCreatedTimestampIfEmpty();

                if (!isInternalTestTraffic()) {
                    publishDeviceMetrics();
                }

                // Refill cache and log warning in case it failed
                const isCacheWriteSuccess = refreshStreamingURLCache(
                    data.streamingUrl
                );
                if (!isCacheWriteSuccess) {
                    await log.warn(
                        "StreamingURL session cache write back failed."
                    );
                }

                return data.streamingUrl;
            } catch (err) {
                await log.error("Failed to consume fresh streaming URL", err);
                throw err;
            }
        },
    });

    return {
        getStreamingUrl: mutationResult.mutateAsync,
        error: mutationResult.error,
        isLoading: mutationResult.isLoading,
    };
};
